import {type MediaCardType} from '@wandb/weave/common/types/media';
import {useEffect} from 'react';

import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {useRampFlagMediaCompareSyncZoomUser} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {useStablePanelId} from '../../Panel/PanelIDContext';
import {type FanOutVariable, type LayoutMode} from '../common';
import {isCompareEligible, isCompareSynced} from './compareViewportEligibility';
import {useCompareViewportStore} from './compareViewportStore';

type Props = {
  config: {
    mode?: LayoutMode;
    compareSettings?: {fanOutVariable?: FanOutVariable};
  };
  mediaType: MediaCardType | null;
  enabled?: boolean;
};

/** Publishes whether a panel is synced and resets its viewport to Fit while it is not. */
export const useCompareViewportLifecycle = ({
  config,
  mediaType,
  enabled = true,
}: Props) => {
  const panelId = useStablePanelId();
  const gateOn = useRampFlagMediaCompareSyncZoomUser() === true;
  const inReport = useIsInLoadedReportContext();
  const {entityName, projectName} = useProjectParams();
  const resetProject = useCompareViewportStore(state => state.resetProject);
  const setViewportInfo = useCompareViewportStore(
    state => state.setViewportInfo
  );
  const resetViewport = useCompareViewportStore(state => state.resetViewport);

  const eligibility = {
    gateOn,
    inReport,
    mode: config.mode,
    mediaType,
    fanOutVariable: config.compareSettings?.fanOutVariable,
  };
  const isEligible = isCompareEligible(eligibility);
  const isSynced = isCompareSynced(eligibility);
  // Loading preserves zoom only while the other synced Compare requirements hold.
  const shouldResetViewport = !isCompareSynced({
    ...eligibility,
    mediaType: mediaType ?? 'image',
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    resetProject(`${entityName}/${projectName}`);
    setViewportInfo(panelId, {isEligible, isSynced});
    if (shouldResetViewport) {
      resetViewport(panelId);
    }
  }, [
    enabled,
    entityName,
    projectName,
    panelId,
    isEligible,
    isSynced,
    shouldResetViewport,
    resetProject,
    setViewportInfo,
    resetViewport,
  ]);
};
